import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Alert,
  Drawer,
  StepContent,
  Card,
  CardContent,
  Stack,
  Collapse,
  LinearProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ChatIcon from '@mui/icons-material/Chat';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import MainLayout from '../../layout/MainLayout';
import { useTranslation } from 'react-i18next';
import { useAppearance } from '../../context/AppearanceContext';
import {
  approveDataRequest,
  completeDataRequest,
  createDataRequest,
  getDataRequests,
  getParameters,
  getWorkingLists,
  rejectDataRequest,
} from '../../services/catalogService';
import { getResolvedOlympAccess } from '../../utils/olympAccess';

import cohortsData from '../../data/cohorts.json';

const Requests = () => {
  const { t, i18n } = useTranslation();
  const { formatDate } = useAppearance();
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('pt')
    ? 'pt-BR'
    : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : 'en-US';
  const olympAccess = getResolvedOlympAccess();
  const canModerate = olympAccess.role === 'admin' || olympAccess.role === 'data_steward';
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [requests, setRequests] = useState([]);
  const [parameterLists, setParameterLists] = useState([]);
  const [availableParameters, setAvailableParameters] = useState([]);
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moderationRequestId, setModerationRequestId] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [requestType, setRequestType] = useState('cohort_creation');
  const [selectedParameterList, setSelectedParameterList] = useState(null);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [requestNotes, setRequestNotes] = useState('');
  const [wizardSuccess, setWizardSuccess] = useState(false);

  const parametersById = useMemo(
    () => new Map(availableParameters.map(parameter => [parameter.id, parameter])),
    [availableParameters]
  );

  const selectedListParameters = useMemo(() => {
    if (!selectedParameterList) {
      return [];
    }

    return selectedParameterList.parameterIds
      .map(id => parametersById.get(id))
      .filter(Boolean);
  }, [parametersById, selectedParameterList]);

  useEffect(() => {
    let isMounted = true;

    async function loadRequestData() {
      setIsLoading(true);
      setLoadError('');

      const [requestsResult, listsResult, parametersResult] = await Promise.all([
        getDataRequests(),
        getWorkingLists(),
        getParameters(),
      ]);

      if (!isMounted) {
        return;
      }

      setRequests(requestsResult.requests || []);
      setParameterLists(listsResult.lists || []);
      setAvailableParameters(parametersResult.parameters || []);

      const sources = [requestsResult, listsResult, parametersResult];
      if (sources.some(result => result?.source === 'error')) {
        setLoadError('Some request data could not be loaded from the backend.');
      }

      setIsLoading(false);
    }

    loadRequestData();

    return () => {
      isMounted = false;
    };
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      'pending_approval': 'warning',
      'in_progress': 'info',
      'in_review': 'info',
      'completed': 'success',
      'rejected': 'error'
    };
    return statusColors[status] || 'default';
  };

  const getRequestTypeLabel = (requestType) => {
    const requestTypeLabels = {
      cohort_creation: t('requests.type.cohortCreation'),
      data_extraction: t('requests.type.dataExtraction'),
      feasibility: t('requests.type.feasibilityReview'),
    };
    return requestTypeLabels[requestType] || requestType;
  };

  const getRequestTypeColor = (requestType) => {
    if (requestType === 'data_extraction') {
      return 'success';
    }
    if (requestType === 'cohort_creation') {
      return 'primary';
    }
    return 'info';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'pending_approval': t('requests.status.pendingApproval'),
      'in_progress': t('requests.status.inProgress'),
      'in_review': t('requests.status.inReview'),
      'completed': t('requests.status.completed'),
      'rejected': t('requests.status.rejected'),
      'feasibility': t('requests.type.feasibilityReview')
    };
    return statusLabels[status] || status;
  };

  const handleToggleRow = (requestId) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
    } else {
      setExpandedRequestId(requestId);
      // Initialize chat messages for this request if not exists
      if (!chatMessages[requestId]) {
        setChatMessages((currentMessages) => ({
          ...currentMessages,
          [requestId]: [
            {
              id: 1,
              sender: t('requests.messages.dataStewardTeam'),
              message: t('requests.messages.underReview'),
              timestamp: new Date().toLocaleString(locale),
              isSystem: true
            }
          ]
        }));
      }
    }
  };

  const handleCreateNew = () => {
    setShowCreateWizard(true);
    setActiveStep(0);
    setWizardSuccess(false);
    // Reset form
    setSelectedCohort(null);
    setRequestType('cohort_creation');
    setSelectedParameterList(null);
    setSelectedParameters([]);
    setRequestNotes('');
  };

  const handleCloseWizard = () => {
    setShowCreateWizard(false);
    setActiveStep(0);
    setWizardSuccess(false);
  };

  const handleSendMessage = (requestId) => {
    const currentMessages = chatMessages[requestId] || [];
    if (chatMessage.trim()) {
      setChatMessages({
        ...chatMessages,
        [requestId]: [
          ...currentMessages,
          {
            id: currentMessages.length + 1,
            sender: t('requests.labels.you'),
            message: chatMessage,
            timestamp: new Date().toLocaleString(locale),
            isSystem: false
          }
        ]
      });
      setChatMessage('');
    }
  };

  const handleParameterListChange = (event) => {
    const listId = Number(event.target.value);
    const list = parameterLists.find(l => l.id === listId);
    setSelectedParameterList(list);

    if (list) {
      const parameters = list.parameterIds
        .map(id => parametersById.get(id))
        .filter(Boolean);
      setSelectedParameters(parameters);
    } else {
      setSelectedParameters([]);
    }
  };

  // Wizard navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmitRequest = async () => {
    if (!selectedCohort || !selectedParameterList) {
      return;
    }

    setIsSubmitting(true);
    setLoadError('');

    try {
      const createdRequest = await createDataRequest({
        title: `${selectedCohort.name} - ${selectedParameterList.name}`,
        requestType,
        workingListId: selectedParameterList.id,
        cohortName: selectedCohort.name,
        cohortReference: selectedCohort.id,
        notes: requestNotes,
        requestPayload: {
          cohortId: selectedCohort.id,
          cohortName: selectedCohort.name,
          estimatedPatients: selectedCohort.patientCount,
          selectedParameterIds: selectedParameters.map(parameter => parameter.id),
          selectedParameterCount: selectedParameters.length,
          selectedParameters: selectedParameters.map(parameter => ({
            id: parameter.id,
            name: parameter.name,
          })),
        },
      });

      setRequests((currentRequests) => [createdRequest, ...currentRequests]);
      setWizardSuccess(true);
      setActiveStep((currentStep) => currentStep + 1);

      window.setTimeout(() => {
        setShowCreateWizard(false);
        setActiveStep(0);
        setWizardSuccess(false);
        setExpandedRequestId(createdRequest.id);
        setChatMessages((currentMessages) => ({
          ...currentMessages,
          [createdRequest.id]: [
            {
              id: 1,
              sender: t('requests.messages.dataStewardTeam'),
              message: t('requests.messages.underReview'),
              timestamp: new Date().toLocaleString(locale),
              isSystem: true
            }
          ]
        }));
      }, 3000);
    } catch (error) {
      setLoadError(error.message || 'Request could not be created.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModerateRequest = async (request, action) => {
    setModerationRequestId(request.id);
    setLoadError('');

    try {
      let updatedRequest = null;

      if (action === 'approve') {
        updatedRequest = await approveDataRequest(request.id);
      } else if (action === 'complete') {
        updatedRequest = await completeDataRequest(request.id);
      } else if (action === 'reject') {
        const reason = window.prompt('Rejection reason', request.rejectionReason || '');
        if (reason === null) {
          setModerationRequestId(null);
          return;
        }
        updatedRequest = await rejectDataRequest(request.id, reason);
      }

      if (updatedRequest) {
        setRequests((currentRequests) =>
          currentRequests.map((currentRequest) =>
            currentRequest.id === updatedRequest.id ? updatedRequest : currentRequest
          )
        );
      }
    } catch (error) {
      setLoadError(error.message || 'Request status could not be updated.');
    } finally {
      setModerationRequestId(null);
    }
  };

  const getTimelineSteps = (request) => {
    const steps = [
      {
        label: t('requests.timeline.submitted'),
        date: request.requestedAt,
        completed: true,
        description: t('requests.timeline.submittedBy', { user: request.requestedBy }),
        details: t('requests.timeline.submittedDetails', { id: request.id })
      },
      {
        label: t('requests.timeline.review'),
        date: request.approvedAt || null,
        completed: !!request.approvedAt,
        description: request.approvedAt ? t('requests.timeline.reviewDone') : t('requests.timeline.reviewPending'),
        details: request.approvedAt ? t('requests.timeline.reviewDoneDetails') : null
      },
      {
        label: t('requests.timeline.approved'),
        date: request.approvedAt,
        completed: !!request.approvedAt,
        description: request.approvedAt ? t('requests.timeline.approvedDone') : null,
        details: request.approvedAt ? t('requests.timeline.approvedDoneDetails') : null
      },
      {
        label: t('requests.timeline.processing'),
        date: null,
        completed: request.status === 'completed',
        description:
          request.status === 'completed'
            ? t('requests.timeline.processingDone')
            : request.status === 'in_progress'
              ? t('requests.timeline.processingInProgress')
              : null,
        details:
          request.status === 'completed' || request.status === 'in_progress'
            ? t('requests.timeline.processingDetails', { count: request.variableCount || 0 })
            : null
      },
      {
        label: t('requests.timeline.provided'),
        date: request.completedAt,
        completed: request.status === 'completed',
        description: request.status === 'completed' ? t('requests.timeline.providedDone') : null,
        details: request.status === 'completed' ? t('requests.timeline.providedDoneDetails') : null
      },
    ];
    return steps;
  };

  // Wizard steps
  const wizardSteps = [
    t('requests.wizard.steps.selectCohort'),
    t('requests.wizard.steps.selectType'),
    t('requests.wizard.steps.selectParameters'),
    t('requests.wizard.steps.notes'),
    t('requests.wizard.steps.summary')
  ];

  // Check if current step is valid
  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return !!selectedCohort;
      case 1:
        return !!requestType;
      case 2:
        return Boolean(selectedParameterList) && (requestType !== 'data_extraction' || selectedParameters.length > 0);
      case 3:
        return true;
      case 4:
        // Summary step - always valid if we got here
        return true;
      default:
        return false;
    }
  };

  const renderWizardStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('requests.wizard.selectCohortHelp')}
            </Alert>
            <FormControl fullWidth error={!selectedCohort && activeStep > 0}>
              <InputLabel>{t('requests.wizard.selectCohortLabel')}</InputLabel>
              <Select
                value={selectedCohort?.id || ''}
                label={t('requests.wizard.selectCohortLabel')}
                onChange={(e) => {
                  const cohort = cohortsData.cohorts.find(c => c.id === e.target.value);
                  setSelectedCohort(cohort);
                }}
              >
                {cohortsData.cohorts.map((cohort) => (
                  <MenuItem key={cohort.id} value={cohort.id}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">{cohort.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cohort.patientCount.toLocaleString(locale)} {t('requests.labels.patients')} • {cohort.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCohort && (
              <Card variant="outlined" sx={{ mt: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" fontWeight="bold" color="success.dark">
                      {t('requests.wizard.cohortSelected')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {selectedCohort.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCohort.patientCount.toLocaleString(locale)} {t('requests.labels.patients')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('requests.wizard.selectTypeHelp')}
            </Alert>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
              >
                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    border: requestType === 'cohort_creation' ? 2 : 1,
                    borderColor: requestType === 'cohort_creation' ? 'primary.main' : 'divider',
                    bgcolor: requestType === 'cohort_creation' ? 'primary.50' : 'inherit',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: requestType === 'cohort_creation' ? 'primary.50' : 'action.hover', transform: 'translateY(-2px)', boxShadow: 2 }
                  }}
                  onClick={() => setRequestType('cohort_creation')}
                >
                  <CardContent>
                    <FormControlLabel
                      value="cohort_creation"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {t('requests.type.cohortCreation')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t('requests.type.cohortCreationHint')}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </CardContent>
                </Card>
                <Card
                  variant="outlined"
                  sx={{
                    border: requestType === 'data_extraction' ? 2 : 1,
                    borderColor: requestType === 'data_extraction' ? 'primary.main' : 'divider',
                    bgcolor: requestType === 'data_extraction' ? 'primary.50' : 'inherit',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: requestType === 'data_extraction' ? 'primary.50' : 'action.hover', transform: 'translateY(-2px)', boxShadow: 2 }
                  }}
                  onClick={() => setRequestType('data_extraction')}
                >
                  <CardContent>
                    <FormControlLabel
                      value="data_extraction"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {t('requests.type.dataExtraction')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t('requests.type.dataExtractionHint')}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </CardContent>
                </Card>
              </RadioGroup>
            </FormControl>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('requests.wizard.selectParametersHelp')}
            </Alert>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('requests.wizard.selectParameterListLabel')}</InputLabel>
              <Select
                value={selectedParameterList?.id || ''}
                label={t('requests.wizard.selectParameterListLabel')}
                onChange={handleParameterListChange}
              >
                {parameterLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">{list.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('requests.wizard.availableParameters', { count: list.parameterIds.length })}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedParameterList && (
              <>
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: requestType === 'data_extraction' ? 2 : 0 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {requestType === 'data_extraction'
                      ? t('requests.wizard.parametersSelected', { count: selectedParameters.length })
                      : `${selectedParameterList.name} (${selectedParameterList.parameterIds.length})`}
                  </Typography>
                </Alert>
                {requestType === 'data_extraction' && (
                  <Autocomplete
                    multiple
                    options={selectedListParameters}
                    getOptionLabel={(option) => option.name}
                    value={selectedParameters}
                    onChange={(event, newValue) => {
                      setSelectedParameters(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('requests.wizard.selectParametersLabel')}
                        placeholder={t('requests.wizard.selectParametersPlaceholder')}
                        error={selectedParameters.length === 0}
                        helperText={selectedParameters.length === 0 ? t('requests.wizard.selectAtLeastOne') : ""}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          size="small"
                          color="primary"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                )}
              </>
            )}

            {!selectedParameterList && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('requests.wizard.selectListFirst')}
              </Alert>
            )}
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('requests.wizard.notesHelp')}
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder={t('requests.wizard.notesPlaceholder')}
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              helperText={t('requests.wizard.notesHelper')}
            />
          </Box>
        );
      case 4:
        // Summary step
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('requests.wizard.reviewBeforeSubmit')}
            </Typography>
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {t('requests.wizard.summary.selectedCohort')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCohort?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCohort?.patientCount?.toLocaleString(locale)} {t('requests.details.patients')}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {t('requests.wizard.summary.requestType')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {requestType === 'cohort_creation'
                      ? t('requests.type.cohortCreation')
                      : t('requests.type.dataExtraction')}
                  </Typography>
                </CardContent>
              </Card>

              {requestType === 'data_extraction' && selectedParameters.length > 0 && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      {t('requests.wizard.summary.selectedParameters')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {selectedParameterList?.name} - {selectedParameters.length} Parameter
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {selectedParameters.slice(0, 10).map((param) => (
                        <Chip key={param.id} label={param.name} size="small" />
                      ))}
                      {selectedParameters.length > 10 && (
                        <Chip
                          label={t('requests.wizard.summary.moreSelected', { count: selectedParameters.length - 10 })}
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {requestNotes && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      {t('requests.wizard.summary.notes')}
                    </Typography>
                    <Typography variant="body2">
                      {requestNotes}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <Alert severity="info" icon={<DescriptionIcon />}>
                {t('requests.wizard.summary.submitHint')}
              </Alert>
            </Stack>
          </Box>
        );
      default:
        return null;
    }
  };

  // Render create wizard
  const renderCreateWizard = () => {
    const progressPercentage = ((activeStep + 1) / wizardSteps.length) * 100;

    return (
      <Drawer
        anchor="right"
        open={showCreateWizard}
        onClose={handleCloseWizard}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600 } }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="bold">
                {t('requests.wizard.newRequestTitle')}
              </Typography>
              <IconButton onClick={handleCloseWizard} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
            {!wizardSuccess && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('requests.wizard.stepOf', { current: activeStep + 1, total: wizardSteps.length })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">
                    {Math.round(progressPercentage)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {wizardSuccess ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {t('requests.wizard.successTitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('requests.wizard.successSubtitle')}
                </Typography>
              </Box>
            ) : (
              <Stepper activeStep={activeStep} orientation="vertical">
                {wizardSteps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>
                      <Typography variant="body1" fontWeight="bold">
                        {label}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {renderWizardStepContent(index)}
                      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                        <Button
                          disabled={index === 0 || isSubmitting}
                          onClick={handleBack}
                          startIcon={<NavigateBeforeIcon />}
                          size="large"
                        >
                          {t('requests.actions.back')}
                        </Button>
                        {index === wizardSteps.length - 1 ? (
                          <Button
                            variant="contained"
                            onClick={handleSubmitRequest}
                            disabled={!isStepValid() || isSubmitting}
                            startIcon={<SendIcon />}
                            size="large"
                          >
                            {t('requests.actions.createRequest')}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!isStepValid() || isSubmitting}
                            endIcon={<NavigateNextIcon />}
                            size="large"
                          >
                            {t('requests.actions.next')}
                          </Button>
                        )}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            )}
          </Box>
        </Box>
      </Drawer>
    );
  };

  // Render expanded row content
  const ExpandedRowContent = ({ request }) => {
    const [tabValue, setTabValue] = useState(0);
    const requestMessages = chatMessages[request.id] || [];

    return (
      <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {request.cohortName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {canModerate && request.status !== 'completed' && request.status !== 'rejected' && (
              <>
                {request.status === 'pending_approval' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleModerateRequest(request, 'approve')}
                    disabled={moderationRequestId === request.id}
                  >
                    Approve
                  </Button>
                )}
                {request.status === 'in_review' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => handleModerateRequest(request, 'complete')}
                    disabled={moderationRequestId === request.id}
                  >
                    Complete
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleModerateRequest(request, 'reject')}
                  disabled={moderationRequestId === request.id}
                >
                  Reject
                </Button>
              </>
            )}
            <Chip
              label={getStatusLabel(request.status)}
              color={getStatusColor(request.status)}
              size="medium"
            />
            <IconButton size="small" onClick={() => setExpandedRequestId(null)}>
              <KeyboardArrowUpIcon />
            </IconButton>
          </Box>
        </Box>

        <Paper sx={{ bgcolor: 'white' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<DescriptionIcon />} label={t('requests.tabs.overview')} iconPosition="start" />
            <Tab icon={<TimelineIcon />} label={t('requests.tabs.timeline')} iconPosition="start" />
            <Tab icon={<ChatIcon />} label={t('requests.tabs.chat')} iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Tab 0: Übersicht */}
            {tabValue === 0 && (
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('requests.details.requestId')}</Typography>
                        <Typography variant="body2" fontWeight="600">{request.id}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('requests.details.type')}</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {getRequestTypeLabel(request.requestType)}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('requests.details.requestedBy')}</Typography>
                        <Typography variant="body2" fontWeight="600">{request.requestedBy}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('requests.details.requestedAt')}</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {formatDate(request.requestedAt, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                      </Box>
                      {request.variableCount ? (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">{t('requests.details.parameters')}</Typography>
                            <Typography variant="body2" fontWeight="600">{request.variableCount}</Typography>
                          </Box>
                        </>
                      ) : null}
                      {request.workingListName ? (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Working list</Typography>
                            <Typography variant="body2" fontWeight="600">{request.workingListName}</Typography>
                          </Box>
                        </>
                      ) : null}
                      {request.estimatedPatients && (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">{t('requests.details.estimatedCases')}</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {request.estimatedPatients.toLocaleString(locale)} {t('requests.details.patients')}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {request.notes && (
                  <Alert severity="info" icon={<DescriptionIcon />}>
                    <Typography variant="body2">{request.notes}</Typography>
                  </Alert>
                )}

                {request.variables?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {t('requests.details.selectedParameters', { count: request.variables.length })}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {request.variables.map((variable) => (
                        <Chip key={variable.id} label={variable.name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            )}

            {/* Tab 1: Timeline */}
            {tabValue === 1 && (
              <Box>
                <Stepper orientation="vertical">
                  {getTimelineSteps(request).map((step, index) => (
                    <Step key={index} active={step.completed} completed={step.completed}>
                      <StepLabel
                        StepIconProps={{
                          sx: {
                            fontSize: '1.75rem',
                            '&.Mui-completed': { color: 'success.main' },
                            '&.Mui-active': { color: 'primary.main' }
                          }
                        }}
                      >
                        <Typography variant="body1" fontWeight={step.completed ? '600' : 'normal'}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Box sx={{ ml: 0, pb: 2 }}>
                          {step.date && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {formatDate(step.date, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })} {t('requests.timeline.timeSuffix')}
                            </Typography>
                          )}
                          {step.description && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {step.description}
                            </Typography>
                          )}
                          {step.details && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              {step.details}
                            </Typography>
                          )}
                          {!step.date && !step.completed && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                {t('requests.timeline.pending')}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {/* Tab 2: Chat */}
            {tabValue === 2 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('requests.tabs.chat')}
                </Typography>
                <Card variant="outlined" sx={{ mb: 2, maxHeight: 400, overflow: 'auto' }}>
                  <CardContent>
                    {requestMessages.map((msg, index) => (
                      <Box key={msg.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: msg.isSystem ? 'primary.main' : 'secondary.main' }}>
                            {msg.sender[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight="bold">
                            {msg.sender}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {msg.timestamp}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 5 }}>
                          {msg.message}
                        </Typography>
                        {index < requestMessages.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('requests.chat.inputPlaceholder')}
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(request.id)}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleSendMessage(request.id)}
                    disabled={!chatMessage.trim()}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              {t('layout.menu.requests')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('requests.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            size="large"
            disabled={isLoading}
          >
            {t('requests.actions.newRequest')}
          </Button>
        </Box>

        {loadError && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        {isLoading && (
          <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />
        )}

        {/* Table */}
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 50 }} />
                <TableCell sx={{ fontWeight: 600 }}>{t('requests.details.requestId')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('requests.table.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('requests.table.cohort')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('requests.table.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('requests.table.parameters')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('requests.table.requestedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {t('requests.empty.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('requests.empty.subtitle')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <React.Fragment key={request.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: expandedRequestId === request.id ? 'action.selected' : 'inherit',
                        '&:hover': { bgcolor: expandedRequestId === request.id ? 'action.selected' : 'action.hover' }
                      }}
                      onClick={() => handleToggleRow(request.id)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRequestId === request.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {request.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRequestTypeLabel(request.requestType)}
                          size="small"
                          variant="outlined"
                          color={getRequestTypeColor(request.requestType)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{request.cohortName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(request.status)}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {request.variableCount ? t('requests.labels.parametersCount', { count: request.variableCount }) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.requestedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={expandedRequestId === request.id} timeout="auto" unmountOnExit>
                          <ExpandedRowContent request={request} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Wizard Drawer */}
      {renderCreateWizard()}
    </MainLayout>
  );
};

export default Requests;
